import { colors } from '@everlywell/leaves';
import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import camelcase from 'camelcase';
import { ProductInfoContext } from 'components/Layout';
import Cookies from 'js-cookie';
import { useContext } from 'react';
import { getNYProducts } from 'utils/api/productsApi';
import {
  getOrder,
  getAuthToken,
  deleteOrderFromStore,
} from 'utils/cartHelpers';
import { CookieNames } from 'utils/constants/cookies';
import { STORE_ROOT, APP_ROOT } from 'utils/constants/urls';
import { GetStateAbbreviations } from 'utils/stateAbbreviations';
import { AxiosErrorResponse } from 'utils/types';

import {
  Order,
  CheckoutData,
  PaymentDetails,
  LineItem,
  BundleParamsType,
  BundleItem,
  Address,
  PSCLocation,
  CheckoutFormData,
} from './types';

const statesData = GetStateAbbreviations({ includeNewYork: true });

// Format state spelling to the state abbreviation
export const formatStateToAbbreviation = (state: string): string =>
  statesData.find((data) => data.value === state)?.id || '';

export const formatAbbreviationToState = (abbreviation: string): string =>
  statesData.find((data) => data.id === abbreviation)?.value || '';

/**
 * Get the current order from localStorage
 *
 * @returns Promise that resolves to the current order
 */
export const fetchCurrentOrder = async (): Promise<Order> => {
  const response = JSON.parse(await getOrder()) as Order;
  return response
    ? Promise.resolve(response)
    : Promise.reject(`Unable to fetch order`);
};

/**
 * Get original price of a line item
 *
 * @param lineItem LineItem Object
 *
 * @returns Number describing base price of a single unit of the line item or null if unavailable
 */
export const originalPrice = (lineItem: LineItem): number | null => {
  if (lineItem?.is_subscription) {
    // FIXME: this hook is not following the rules of hooks for being used.
    // Since this is already in prod, and to avoid introducing a new bug, we are disabling the eslint rule for now.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const productSlugArray = Object.values(useContext(ProductInfoContext));
    const masterProduct = productSlugArray?.find(
      (product) => product.name === lineItem.product_name,
    );
    return masterProduct?.price ? parseFloat(masterProduct.price) : null;
  } else {
    return lineItem?.base_price ? parseFloat(lineItem?.base_price) : null;
  }
};

/**
 * Cleans up the completed order by removing it from store
 */
export const cleanupOrder = (): void => {
  deleteOrderFromStore();
};

/**
 * Process order from Checkout page with specified payment method
 *
 * @param order Order Object
 * @param checkoutData Data from the checkout Form or payment service
 * @param paymentDetails Object containing payment-method-specific details
 *
 * @returns Promise that will resolve with a completed Order object
 */
export const processOrder = async (
  order: Order,
  checkoutData: CheckoutData,
  payment: PaymentDetails | null,
): Promise<AxiosResponse> => {
  try {
    const url = `${STORE_ROOT}/aapi/v2/orders/${order.id}/process_order`;
    const authToken = await getAuthToken();
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };
    let data = {};

    // For PayPal subscription orders
    if (payment && payment.billing_agreement_id) {
      data = {
        synchronous: 'true',
        payment,
        ...checkoutData,
        // Billing agreement id is added here to match the old checkout process
        billing_agreement_id: payment.billing_agreement_id,
      };
    } else {
      // For all other orders
      data = {
        synchronous: 'true',
        payment,
        ...checkoutData,
      };
    }

    const requestOpts: AxiosRequestConfig = {
      data,
      headers,
      method: 'PUT',
      url: url,
    };

    return await axios(requestOpts);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getNearestLabs = async (
  zipCode: string,
): Promise<AxiosResponse> => {
  try {
    const url = `${STORE_ROOT}/aapi/v1/pscs/search/${zipCode}`;
    const authToken = await getAuthToken();
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    const requestOpts: AxiosRequestConfig = {
      headers,
      method: 'GET',
      url: url,
    };

    return await axios(requestOpts);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const addVariantToOrder = async (
  variantId: number,
  quantity: number,
  orderId: number,
): Promise<AxiosResponse> => {
  const data = {
    quantity,
    variant_id: variantId,
  };
  try {
    const url = `${STORE_ROOT}/aapi/v1/orders/${orderId}/line_items?location=${window.location}`;
    const authToken = await getAuthToken();
    const requestOpts: AxiosRequestConfig = {
      data,
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      method: 'POST',
      url,
    };
    return await axios(requestOpts);
  } catch (error) {
    if ((error as AxiosErrorResponse).response?.data.status === 404) {
      cleanupOrder();
    }
    return Promise.reject(error);
  }
};

/**
 * Redirects the user to their order confirmation page
 *
 * @param id the `order_id` from Store
 * @param registration_token secret token for viewing the confirmation page
 *
 * @return void
 */
export const redirectToConfirmation = (
  id: number,
  registration_token: string,
): void => {
  window.location.href = `${APP_ROOT}/confirmation?orderId=${id}&registration_token=${registration_token}&referral=true`;
};

/**
 * Builds an object in the shape that the backend API is expecting for the process_order endpoint
 *
 * @param data Raw data collected from the for
 * @param useDiffBilling If true, attaches a bill_address property object
 * @returns Object in shape that backend API is expecting
 */
export const buildCheckoutData = (
  data: CheckoutFormData,
  useDiffBilling: boolean,
): CheckoutData => {
  const ipCookie = Cookies.get(CookieNames.UserIp);
  const campaignId = Cookies.get(CookieNames.IterableEmailCampaignId);
  const templateId = Cookies.get(CookieNames.IterableTemplateId);
  const messageId = Cookies.get(CookieNames.IterableMessageId);

  // Format state to send abbreviated state to the backend
  const billState = formatStateToAbbreviation(data.billState);
  const shipState = formatStateToAbbreviation(data.shipState);

  let checkoutData: CheckoutData = {
    email: data.email,
    marketing: data.marketing ? '1' : '0',
    terms: '1',
    ship_address: {
      country: 'US',
      type: 'shipping',
      city: data.shipCity,
      first_name: data.shipFirstName,
      last_name: data.shipLastName,
      phone: data.shipPhone,
      state: shipState,
      street1: data.shipAddress1,
      street2: data.shipAddress2,
      zipcode: data.shipZipcode,
    },
    smsOptIn: !!data.smsOptIn,
    cookie_data: {
      ipCookie: ipCookie,
      campaignId: campaignId,
      templateId: templateId,
      messageId: messageId,
    },
    bill_address: {
      country: 'US',
      type: 'billing',
      city: useDiffBilling ? data.billCity : data.shipCity,
      first_name: useDiffBilling ? data.billFirstName : data.shipFirstName,
      last_name: useDiffBilling ? data.billLastName : data.shipLastName,
      phone: useDiffBilling ? data.billPhone : data.shipPhone,
      state: useDiffBilling ? billState : shipState,
      street1: useDiffBilling ? data.billAddress1 : data.shipAddress1,
      street2: useDiffBilling ? data.billAddress2 : data.shipAddress2,
      zipcode: useDiffBilling ? data.billZipcode : data.shipZipcode,
    },
    attribution: data.attribution,
  };

  // If order has in-lab kits & a Patient Service Center (PSC) is selected, include PSC id in check out data
  if (data.psc_id) {
    checkoutData = { ...checkoutData, psc_id: data.psc_id };
  }

  return checkoutData;
};

export const displayLineItemsQty = (
  lineItems: LineItem[] | undefined,
): number => {
  let totalQuantity;
  if (lineItems) {
    totalQuantity = lineItems?.reduce(function (accumulator, item) {
      return accumulator + item?.quantity;
    }, 0);
  }
  return totalQuantity || 0;
};

export const isEmptyOrder = (order: Order): boolean =>
  order.line_items.length === 0;

/**
 * Is this an anonymous guest order with no logged-in user attached?
 *
 * @param order Order object
 * @returns Boolean stating true if the order has no user attached
 */
export const isGuestOrder = (order: Order): boolean => !order.user_id;

/**
 * Get the current order from Store API
 *
 * @param orderId number
 * @returns Promise resolving to the order as it exists in the database
 */
export const fetchOrderFromApi = async (
  orderId: number,
): Promise<AxiosResponse> => {
  const url = `${STORE_ROOT}/aapi/v2/orders/${orderId}`;
  const authToken = await getAuthToken();

  const requestOpts: AxiosRequestConfig = {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
    method: 'GET',
    url,
  };

  return axios(requestOpts);
};

/**
 * Get the current order from Store API using registration token
 *
 * @param orderId number
 * @param registrationToken string
 * @returns Promise resolving to the order as it exists in the database
 */
export const fetchOrderFromApiByRegistrationToken = async (
  orderId: number,
  registrationToken: string,
): Promise<AxiosResponse> => {
  const url = `${STORE_ROOT}/aapi/v2/orders/${orderId}/token?registration_token=${registrationToken}`;
  const authToken = await getAuthToken();

  const requestOpts: AxiosRequestConfig = {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
    method: 'GET',
    url,
  };

  return axios(requestOpts);
};

/**
 * Get the shipping or billing address info for an order from Store API
 *
 * @param orderId number
 * @param addressId number
 * @returns Promise resolving to the address as it exists in the database
 */
export const fetchAddressFromApi = async (
  orderId: number,
  addressId: number,
): Promise<AxiosResponse> => {
  const url = `${STORE_ROOT}/aapi/v2/orders/${orderId}/addresses/${addressId}`;
  const authToken = await getAuthToken();

  const requestOpts: AxiosRequestConfig = {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
    method: 'GET',
    url,
  };

  return axios(requestOpts);
};

/**
 * Fill out the address fields on the checkout form
 *
 * @param address address object
 * @param type string: ship or bill
 * @returns void
 */
export const fillAddress = (address: Address, type: string): void => {
  // Works around mismatch between our APIs naming and our input field IDs
  const addr = Object.assign(address);
  addr['address1'] = addr['street1'];
  addr['address2'] = addr['street2'];
  const addressKeys = [
    'city',
    'first_name',
    'last_name',
    'state',
    'address1',
    'address2',
    'zipcode',
    'phone',
  ];
  addressKeys.forEach((key) => {
    if (addr[key]) {
      // Generates a selector in the format "#shipFirstName"
      const selector = `#${camelcase(`${type}_${key}`)}`;
      // States are stored as abbreviations by the API but displayed in full
      const val =
        key === 'state' ? formatAbbreviationToState(addr[key]) : addr[key];
      if (document.querySelector(selector)) {
        (document.querySelector(selector) as HTMLInputElement).value =
          val.toString();
      }
    }
  });
};

/**
 * Fill out the address fields on the checkout form
 *
 * @param order Order object
 * @returns promise that resolves if the address fills successfully
 */
export const fillAddressFromApi = async (order: Order): Promise<void> => {
  try {
    (document.querySelector('#email') as HTMLInputElement).value = order.email;
    if (order?.ship_address_id) {
      const address = await fetchAddressFromApi(
        order.id,
        order.ship_address_id,
      );
      fillAddress(address.data, 'ship');
    }
    if (order?.bill_address_id) {
      const address = await fetchAddressFromApi(
        order.id,
        order.bill_address_id,
      );
      fillAddress(address.data, 'bill');
    }
  } catch (error) {
    return Promise.reject(error);
  }
};

export const updateFailedOrderGuestToken = async (
  order: Order,
): Promise<AxiosResponse> => {
  try {
    const url = `${STORE_ROOT}/aapi/v2/orders/${order.id}/token_update?registration_token=${order.registration_token}`;
    const authToken = await getAuthToken();
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    const data = {};

    const requestOpts: AxiosRequestConfig = {
      data,
      headers,
      method: 'PUT',
      url: url,
    };

    return await axios(requestOpts);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const completeOrder = async (
  order: Order,
  checkoutData: CheckoutData,
  payment: PaymentDetails | null,
): Promise<AxiosResponse> => {
  try {
    const url = `${STORE_ROOT}/aapi/v2/orders/${order.id}/complete`;
    const authToken = await getAuthToken();
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    const data = {
      ...payment,
      terms: checkoutData.terms,
      marketing: checkoutData.marketing,
    };

    const requestOpts: AxiosRequestConfig = {
      data,
      headers,
      method: 'PUT',
      url: url,
    };

    return await axios(requestOpts);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const salesTaxQuotation = async (
  orderId: number | undefined,
  addresses: { address1: string; city: string; state: string; zipcode: string },
  email: string,
): Promise<AxiosResponse> => {
  try {
    const url = `${STORE_ROOT}/aapi/v2/orders/${orderId}/quote_tax`;
    const authToken = await getAuthToken();

    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    const data = {
      addresses: { ...addresses },
      email: email,
    };

    const requestOpts: AxiosRequestConfig = {
      data,
      headers,
      method: 'POST',
      url: url,
    };

    return axios(requestOpts);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const addPromoDiscountToOrder = async (
  orderId: number,
  promoCode: string,
): Promise<AxiosResponse> => {
  try {
    const url = `${STORE_ROOT}/aapi/v2/orders/${orderId}/add_promo`;
    const authToken = await getAuthToken();

    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    const data = {
      coupon_code: promoCode,
    };

    const requestOpts: AxiosRequestConfig = {
      data,
      headers,
      method: 'PUT',
      url: url,
    };

    return axios(requestOpts);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const addShippingMethodToOrder = async (
  orderId: number,
  shippingMethodId: string,
): Promise<AxiosResponse> => {
  try {
    const url = `${STORE_ROOT}/aapi/v2/orders/${orderId}/add_shipping`;
    const authToken = await getAuthToken();

    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    const data = {
      shipping_method_id: shippingMethodId,
    };

    const requestOpts: AxiosRequestConfig = {
      data,
      headers,
      method: 'PUT',
      url: url,
    };

    return axios(requestOpts);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const addGiftCardToOrder = async (
  orderId: number,
  giftCardCode: string,
): Promise<AxiosResponse> => {
  try {
    const url = `${STORE_ROOT}/aapi/v2/orders/${orderId}/apply_gift_card`;
    const authToken = await getAuthToken();

    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    const data = {
      giftcard_code: giftCardCode,
    };

    const requestOpts: AxiosRequestConfig = {
      data,
      headers,
      method: 'PUT',
      url: url,
    };

    return axios(requestOpts);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const removeDiscountFromOrder = async (
  orderId: number,
): Promise<AxiosResponse> => {
  try {
    const url = `${STORE_ROOT}/aapi/v2/orders/${orderId}/remove_promos`;
    const authToken = await getAuthToken();

    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    const requestOpts: AxiosRequestConfig = {
      headers,
      method: 'PUT',
      url: url,
    };

    return axios(requestOpts);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const removeGiftCardFromOrder = async (
  orderId: number,
  adjustmentId: number,
): Promise<AxiosResponse> => {
  try {
    const url = `${STORE_ROOT}/aapi/v2/orders/${orderId}/remove_giftup_gift_card`;
    const authToken = await getAuthToken();

    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    const data = {
      adjustment_id: adjustmentId,
    };

    const requestOpts: AxiosRequestConfig = {
      data,
      headers,
      method: 'PUT',
      url: url,
    };

    return axios(requestOpts);
  } catch (error) {
    return Promise.reject(error);
  }
};

const numbersOnly = (str: string): RegExpMatchArray | null => {
  const onlyNumbers = /[0-9]/;
  return str.match(onlyNumbers);
};

export const formatPhoneNumber = (value: string): string => {
  const input = value.replace(/\D/g, '').substring(0, 10); // First ten digits of input only
  const areaCode = input.substring(0, 3);
  const middle = input.substring(3, 6);
  const last = input.substring(6, 10);

  if (numbersOnly(input) && input.length > 6) {
    return `(${areaCode}) ${middle}-${last}`;
  } else if (numbersOnly(input) && input.length > 3) {
    return `(${areaCode}) ${middle}`;
  } else if (numbersOnly(input) && input.length > 0) {
    return `(${areaCode}`;
  }

  return '';
};

export const formatZipCode = (value: string): string => {
  const input = value.replace(/\D/g, '').substring(0, 9); // First five digits of input only
  const firstFive = input.substring(0, 5);
  const lastFour = input.substring(5, 9);

  if (numbersOnly(input) && input.length > 5) {
    return `${firstFive}-${lastFour}`;
  } else if (numbersOnly(input) && input.length <= 5) {
    return `${firstFive}`;
  } else {
    return '';
  }
};

export const orderQuantity = (lineItems: LineItem[]): number => {
  let quantity = 0;

  lineItems?.forEach((lineItem) => {
    quantity = quantity + lineItem.quantity;
  });

  return quantity;
};

// For Google Address Autocomplete (below this line)----------------
let autocomplete: google.maps.places.Autocomplete;
let address1Field: HTMLInputElement;
let address2Field: HTMLInputElement;
let postalField: HTMLInputElement;

const fillInAddress = () => {
  // Get the place details from the autocomplete object.
  const place = autocomplete.getPlace();
  let address1 = '';
  let postcode = '';

  // Get each component of the address from the place details,
  // and then fill-in the corresponding field on the form.
  // place.address_components are google.maps.GeocoderAddressComponent objects
  // which are documented at http://goo.gle/3l5i5Mr
  for (const component of place.address_components as google.maps.GeocoderAddressComponent[]) {
    const componentType = component.types[0];

    switch (componentType) {
      case 'street_number': {
        address1 = `${component.long_name} ${address1}`;
        break;
      }

      case 'route': {
        address1 += component.short_name;
        break;
      }

      case 'postal_code': {
        postcode = `${component.long_name}${postcode}`;
        break;
      }

      case 'locality':
        (document.querySelector('#shipCity') as HTMLInputElement).value =
          component.long_name;
        break;

      case 'administrative_area_level_1': {
        const shipState = document.querySelector(
          '#shipState',
        ) as HTMLInputElement;
        shipState.style.color = `${colors.gray4}`;
        shipState.value = component.long_name;
        break;
      }
    }
  }

  address1Field.value = address1;
  postalField.value = postcode;

  // After filling the form with address components from the Autocomplete
  // prediction, set cursor focus on the second address line to encourage
  // entry of subpremise information such as apartment, unit, or floor number.
  address2Field.focus();
};

const initAutocomplete = () => {
  address1Field = document.querySelector('#shipAddress1') as HTMLInputElement;
  address2Field = document.querySelector('#shipAddress2') as HTMLInputElement;
  postalField = document.querySelector('#shipZipcode') as HTMLInputElement;

  // We are skipping the default behavior of the enter key (submitting the form) for this field
  // since the user's intention is to select an address from the Googles dropdown.
  address1Field.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  });

  // Create the autocomplete object, restricting the search predictions to
  // addresses in the US.
  autocomplete = new google.maps.places.Autocomplete(address1Field, {
    componentRestrictions: { country: ['us'] },
    fields: ['address_components', 'geometry'],
    types: ['address'],
  });

  // When the user selects an address from the drop-down, populate the
  // address fields in the form.
  autocomplete.addListener('place_changed', fillInAddress);
};

// Load Google Maps API script
export const initGoogleAutocomplete =
  (): EventListenerOrEventListenerObject | void => {
    const onLoad = () => initAutocomplete();
    if (!window.google) {
      const script = document.createElement(`script`);
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY}&libraries=places`;
      script.defer = true;
      document.head.append(script);
      script.addEventListener(`load`, onLoad);
      return () => script.removeEventListener(`load`, onLoad);
    } else onLoad();
  };
// For Google Address Autocomplete (above this line)----------------

export const isZeroBalance = (order?: Order): boolean =>
  order?.total === '0.00';

const PROMO_STRING = 'promo-code=';

/**
 * We utilize links with url params to bundle items into the cart on checkout page
 * ex: /checkout?121:1 should build a cart with qty 1 of product_variant with an id of 121
 * @param location string
 * @returns object containing array of variant/qty combos to add and any promos added
 *
 */
export const getBundledParams = (urlParams: string): BundleParamsType => {
  const bundleItems: Array<BundleItem> = [];
  let promoToApply;

  if (urlParams && urlParams.indexOf(':') !== -1) {
    const variantParams = urlParams.split('&');

    variantParams.forEach((vp) => {
      const combo = vp.replace(/\?/i, '');
      const [variantId, qty] = combo.split(':');
      if (variantId && qty) {
        bundleItems.push({
          variantId: parseInt(variantId),
          qty: parseInt(qty),
        });
      }
    });
  }

  if (urlParams && urlParams.indexOf(PROMO_STRING) !== -1) {
    const [, coupon] = urlParams.split(PROMO_STRING);
    if (coupon) {
      promoToApply = coupon;
    }
  }

  return {
    bundleItems,
    promoToApply,
  };
};

export const addBundledItems = async (
  items: Array<BundleItem>,
  orderId: number,
): Promise<AxiosResponse<Record<string, unknown>>[]> => {
  try {
    const lineItemsToAdd = items.map((i) =>
      addVariantToOrder(i.variantId, i.qty, orderId),
    );
    return await Promise.all(lineItemsToAdd);
  } catch (e) {
    return Promise.reject('Unable to add item bundle to order');
  }
};

/**
 * Remove unsafe characters read in from URL parameters used to modify order
 *
 * @param str string
 * @returns string
 */
export const stripUnsafeChars = (str: string): string =>
  str.replace(/[^A-Za-z0-9._~()'!*:@,;+?&=-]/g, '');
//

export const hasSelectedPscLocation = (psc: PSCLocation): boolean =>
  !Object.values(psc).includes('');

/**
 *
 * @deprecated - use react-Query
 * @example
 * const {data}=  useQuery(`ny-products`, () => getNYProducts(),{
    cacheTime: Infinity,
    staleTime: Infinity
  });
 */
export const isOrderAllowedInNewYork = async (
  order: Order | undefined,
): Promise<boolean> => {
  if (!order) {
    return false;
  }

  const nyProducts = await getNYProducts();
  const nyProductsSkus = nyProducts.map((nyProduct) => nyProduct.sku);

  const lineItems = order.line_items;

  const isNotAllowed = lineItems.some((item) => {
    if (
      !nyProductsSkus.includes(item.variant_sku) &&
      !nyProductsSkus.includes(item.product_sku)
    ) {
      return true;
    }
    return false;
  });

  return !isNotAllowed;
};
